import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { MAP_ROOT } from '@lassi/config';
import { Point } from 'leaflet';
import { MapService, SelectMode } from './map';
import { calculateIdentifyBounds } from './map/coordinate.utils';
import { RouteUtilsService } from './route-utils.service';
import { SelectSurveyMarkStore } from './select-survey-mark.store';

@Injectable({
  providedIn: 'root',
})
export class SelectSurveyMarkService {
  private readonly mapService = inject(MapService);
  private readonly selectSurveyMarkStore = inject(SelectSurveyMarkStore);
  private readonly router = inject(Router);
  private readonly routeUtilsService = inject(RouteUtilsService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly mapRoot = inject(MAP_ROOT);

  /**
   * Starts the selection process for the specified action
   * @param action - Whether to add or remove survey marks
   */
  public startSelectingSurveyMarksFromMap(action: 'add' | 'remove'): void {
    const mapMode = this.mapService.switchMapMode(SelectMode, {
      stopTooltip: 'Stop selecting survey marks',
    });

    mapMode.mapClick$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: ({ event, map }) => {
        const point = new Point(
          Math.round(event.containerPoint.x),
          Math.round(event.containerPoint.y),
        );

        this.selectSurveyMarkStore.selectSurveyMarks({
          bounds: calculateIdentifyBounds(point, map),
          action,
        });
      },
      complete: () => {
        if (!this.routeUtilsService.isDrawerActive()) {
          this.goBackToSelectSurveyMarkPage();
        }
      },
    });
  }

  private goBackToSelectSurveyMarkPage(): void {
    this.router.navigate(
      [
        this.mapRoot,
        {
          outlets: {
            [RouteUtilsService.MAP_MENU_OUTLET_NAME]: [
              RouteUtilsService.MAP_MENU_ROOT_PATH,
              'survey-mark-select',
            ],
          },
        },
      ],
      {
        queryParamsHandling: 'merge',
      },
    );
  }
}
